import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Star, StarOff, Upload, Plus, LogOut, EyeOff, Eye, DollarSign, Music2, PlayCircle, Gift, BarChart3, HandHeart, Package, X, Check } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const emptyForm = {
  title: "", artist: "Sanctum", album: "", genre: "Worship", description: "",
  verse: "", verse_ref: "",
  cover_url: "", audio_url: "", video_url: "",
  price_mp3: 1.99, price_mp4: 2.99, featured: false, published: true,
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [bulkFiles, setBulkFiles] = useState([]); // [{stem, audio, cover, video, title, uploading}]
  const [bulkBusy, setBulkBusy] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("sanctum_token") : null;
  useEffect(() => {
    // Cookie-based session (Google OAuth) or JWT bearer both check via /auth/me
    api.get("/auth/me").catch(() => {
      if (!token) nav("/admin/login");
    });
  }, [token, nav]);

  const load = () => api.get("/admin/songs").then((r) => setSongs(r.data)).catch((e) => {
    if (e?.response?.status === 401) { localStorage.removeItem("sanctum_token"); nav("/admin/login"); }
  });
  const loadAnalytics = () => api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  const loadPrayers = () => api.get("/admin/prayer-requests").then((r) => setPrayers(r.data)).catch(() => {});
  const markPrayer = async (id) => { await api.patch(`/admin/prayer-requests/${id}`); loadPrayers(); };

  useEffect(() => { load(); loadAnalytics(); loadPrayers(); }, []);

  const upload = async (kind, file) => {
    setUploadingKind(kind);
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (kind === "cover") setForm((f) => ({ ...f, cover_url: full }));
      else if (kind === "audio") setForm((f) => ({ ...f, audio_url: full }));
      else if (kind === "video") setForm((f) => ({ ...f, video_url: full }));
      toast.success(`${kind} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKind(null); }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.cover_url || !form.audio_url) {
      toast.error("Title, cover and audio are required"); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/songs", form);
      toast.success("Song published");
      setForm(emptyForm); load(); loadAnalytics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const patch = async (id, upd) => { await api.put(`/admin/songs/${id}`, upd); load(); };
  const remove = async (id) => {
    if (!window.confirm("Delete this song?")) return;
    await api.delete(`/admin/songs/${id}`); load(); loadAnalytics();
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("sanctum_token");
    nav("/admin/login");
  };

  // --- Bulk upload ---
  const AUDIO_EXT = /\.(mp3|wav|m4a|ogg|flac|aac)$/i;
  const VIDEO_EXT = /\.(mp4|mov|webm|m4v)$/i;
  const IMAGE_EXT = /\.(jpg|jpeg|png|webp|gif)$/i;
  const stemOf = (name) => name.replace(/\.[^.]+$/, "").trim();

  const onBulkFiles = (files) => {
    const map = new Map();
    for (const f of Array.from(files)) {
      const s = stemOf(f.name);
      const row = map.get(s) || { stem: s, title: s, audio: null, cover: null, video: null };
      if (AUDIO_EXT.test(f.name)) row.audio = f;
      else if (VIDEO_EXT.test(f.name)) row.video = f;
      else if (IMAGE_EXT.test(f.name)) row.cover = f;
      map.set(s, row);
    }
    setBulkFiles((prev) => [...prev, ...Array.from(map.values())]);
  };

  const removeBulkRow = (i) => setBulkFiles((rows) => rows.filter((_, idx) => idx !== i));

  const publishBulk = async () => {
    const rows = bulkFiles.filter((r) => r.audio);
    if (rows.length === 0) { toast.error("Add at least one audio file"); return; }
    setBulkBusy(true);
    try {
      const uploadOne = async (file, kind) => {
        const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      };
      const songs = [];
      for (const r of rows) {
        const audio_url = await uploadOne(r.audio, "audio");
        const cover_url = r.cover
          ? await uploadOne(r.cover, "cover")
          : "https://images.unsplash.com/photo-1514519273132-6a1abd48302c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
        const video_url = r.video ? await uploadOne(r.video, "video") : undefined;
        songs.push({ title: r.title || r.stem, cover_url, audio_url, video_url });
      }
      await api.post("/admin/songs/bulk", { songs });
      toast.success(`Published ${songs.length} song${songs.length > 1 ? "s" : ""}`);
      setBulkFiles([]);
      load(); loadAnalytics();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk publish failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[--sanctum-bg]">
      <div className="border-b border-[--sanctum-border] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl">Sanctum</span>
            <span className="eyebrow">Admin</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-pill btn-outline text-xs">
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        {/* Analytics */}
        {analytics && (
          <section data-testid="analytics-panel">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 size={16} strokeWidth={1.5} className="text-neutral-500" />
              <p className="eyebrow">Last 30 days</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat icon={<DollarSign size={16} strokeWidth={1.5} />} label="Revenue" value={`$${analytics.total_revenue.toFixed(2)}`} testId="stat-revenue" />
              <Stat icon={<DollarSign size={16} strokeWidth={1.5} />} label="Offerings" value={`$${analytics.total_offerings.toFixed(2)}`} testId="stat-offerings" />
              <Stat icon={<Music2 size={16} strokeWidth={1.5} />} label="Purchases" value={analytics.total_purchases} testId="stat-purchases" />
              <Stat icon={<Gift size={16} strokeWidth={1.5} />} label="Gifts sent" value={analytics.total_gifts} testId="stat-gifts" />
              <Stat icon={<PlayCircle size={16} strokeWidth={1.5} />} label="Total plays" value={analytics.total_plays} testId="stat-plays" />
            </div>
            <div className="mt-6 rounded-2xl bg-white border border-[--sanctum-border] p-6">
              <p className="eyebrow mb-4">Daily revenue</p>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <AreaChart data={analytics.daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1C1C19" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#1C1C19" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DF" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9E9E9C" }} tickFormatter={(d) => d.slice(5)} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: "#9E9E9C" }} />
                    <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} labelFormatter={(l) => l} contentStyle={{ borderRadius: 12, border: "1px solid #EAE6DF", fontSize: 12 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#1C1C19" strokeWidth={1.5} fill="url(#rev)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            {analytics.top_songs?.length > 0 && (
              <div className="mt-6 rounded-2xl bg-white border border-[--sanctum-border] p-6">
                <p className="eyebrow mb-4">Top by plays</p>
                <div className="space-y-2">
                  {analytics.top_songs.map((s, i) => (
                    <div key={s.id} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-neutral-400 tabular-nums">{i + 1}</span>
                      {s.cover_url && <img src={s.cover_url} alt="" className="w-8 h-8 rounded object-cover" />}
                      <div className="flex-1 truncate">{s.title}</div>
                      <div className="tabular-nums text-neutral-500">{s.plays} plays</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Prayer inbox */}
        <section data-testid="prayer-inbox">
          <div className="flex items-center gap-2 mb-5">
            <HandHeart size={16} strokeWidth={1.5} className="text-[--sanctum-accent]" />
            <p className="eyebrow">Prayer inbox</p>
            {prayers.filter((p) => !p.read).length > 0 && (
              <span className="text-[10px] tracking-widest uppercase bg-[--sanctum-accent] text-neutral-900 px-2 py-0.5 rounded-full">
                {prayers.filter((p) => !p.read).length} new
              </span>
            )}
          </div>
          {prayers.length === 0 ? (
            <div className="rounded-2xl bg-white border border-[--sanctum-border] p-8 text-sm text-neutral-500">
              No prayer requests yet.
            </div>
          ) : (
            <div className="space-y-3">
              {prayers.slice(0, 12).map((p) => (
                <div
                  key={p.id}
                  data-testid={`prayer-item-${p.id}`}
                  className={`rounded-2xl border p-5 bg-white ${p.read ? "border-[--sanctum-border] opacity-70" : "border-[--sanctum-accent]/40"}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-xs text-neutral-500 tracking-wide">
                        {p.name || "Anonymous"}{p.email && <span> · {p.email}</span>}
                        <span className="ml-2 text-neutral-400">{p.created_at?.slice(0, 10)}</span>
                      </div>
                      <p className="mt-2 text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap">{p.request}</p>
                    </div>
                    {!p.read && (
                      <button
                        data-testid={`prayer-read-${p.id}`}
                        onClick={() => markPrayer(p.id)}
                        title="Mark as prayed for"
                        className="p-2 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100"
                      >
                        <Check size={14} strokeWidth={1.75} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bulk uploader */}
        <section data-testid="bulk-uploader">
          <div className="flex items-center gap-2 mb-5">
            <Package size={16} strokeWidth={1.5} className="text-neutral-500" />
            <p className="eyebrow">Bulk uploader</p>
          </div>
          <div className="rounded-2xl bg-white border border-[--sanctum-border] p-6">
            <p className="text-sm text-neutral-600 mb-4">
              Drop a folder of MP3s (and matching cover images with the same filename) to publish many songs at once.
            </p>
            <label
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onBulkFiles(e.dataTransfer.files); }}
              className="block border-2 border-dashed border-[--sanctum-border] rounded-xl p-8 text-center cursor-pointer hover:border-neutral-400 transition-colors"
              data-testid="bulk-dropzone"
            >
              <Upload size={20} strokeWidth={1.5} className="mx-auto text-neutral-400" />
              <p className="text-sm text-neutral-600 mt-2">Click to select or drag & drop files</p>
              <p className="text-xs text-neutral-400 mt-1">Audio: mp3, wav, m4a · Image: jpg, png · Video: mp4</p>
              <input
                type="file" multiple hidden
                data-testid="bulk-file-input"
                accept="audio/*,image/*,video/*"
                onChange={(e) => onBulkFiles(e.target.files)}
              />
            </label>

            {bulkFiles.length > 0 && (
              <div className="mt-6 space-y-2">
                {bulkFiles.map((r, i) => (
                  <div key={i} data-testid={`bulk-row-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 border border-neutral-100">
                    <input
                      value={r.title}
                      onChange={(e) => setBulkFiles((rows) => rows.map((row, idx) => idx === i ? { ...row, title: e.target.value } : row))}
                      className="flex-1 h-9 px-3 text-sm rounded-lg border border-[--sanctum-border] bg-white focus:outline-none"
                    />
                    <div className="text-[10px] tracking-widest uppercase text-neutral-500 flex gap-2">
                      {r.audio && <span className="text-emerald-700">Audio</span>}
                      {r.cover && <span className="text-emerald-700">Cover</span>}
                      {r.video && <span className="text-emerald-700">Video</span>}
                      {!r.audio && <span className="text-neutral-400">no audio</span>}
                    </div>
                    <button
                      onClick={() => removeBulkRow(i)}
                      className="p-1.5 text-neutral-400 hover:text-neutral-900"
                    ><X size={13} strokeWidth={1.5} /></button>
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  <button
                    data-testid="bulk-publish-btn"
                    onClick={publishBulk}
                    disabled={bulkBusy}
                    className="btn-pill btn-primary text-sm"
                  >
                    {bulkBusy ? "Publishing…" : `Publish ${bulkFiles.filter((r) => r.audio).length} songs`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* New song form */}
          <form onSubmit={create} className="lg:col-span-1 bg-white rounded-2xl border border-[--sanctum-border] p-6 h-fit sticky top-6">
            <h2 className="font-display text-2xl mb-1">Publish a song</h2>
            <p className="text-xs text-neutral-500 mb-6">Upload media or paste external URLs.</p>

            <div className="space-y-3">
              <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="admin-title" />
              <Field label="Artist" value={form.artist} onChange={(v) => setForm({ ...form, artist: v })} testId="admin-artist" />
              <Field label="Album" value={form.album} onChange={(v) => setForm({ ...form, album: v })} testId="admin-album" />
              <Field label="Genre" value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} testId="admin-genre" />
              <div>
                <label className="eyebrow block mb-1.5">Description</label>
                <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="admin-description"
                  className="w-full p-2.5 text-sm rounded-lg border border-[--sanctum-border]" />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Verse / Story</label>
                <textarea rows={2} value={form.verse} onChange={(e) => setForm({ ...form, verse: e.target.value })}
                  data-testid="admin-verse"
                  placeholder="A quiet line from scripture or the story behind the song."
                  className="w-full p-2.5 text-sm rounded-lg border border-[--sanctum-border]" />
              </div>
              <Field label="Reference (e.g. Psalm 23:1)" value={form.verse_ref} onChange={(v) => setForm({ ...form, verse_ref: v })} testId="admin-verse-ref" />

              <UploadRow label="Cover art" kind="cover" accept="image/*" busy={uploadingKind==="cover"} url={form.cover_url}
                onFile={(f) => upload("cover", f)} onUrl={(v) => setForm({ ...form, cover_url: v })} />
              <UploadRow label="Audio (MP3)" kind="audio" accept="audio/*" busy={uploadingKind==="audio"} url={form.audio_url}
                onFile={(f) => upload("audio", f)} onUrl={(v) => setForm({ ...form, audio_url: v })} />
              <UploadRow label="Video (MP4)" kind="video" accept="video/*" busy={uploadingKind==="video"} url={form.video_url}
                onFile={(f) => upload("video", f)} onUrl={(v) => setForm({ ...form, video_url: v })} optional />

              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    data-testid="admin-featured" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    data-testid="admin-published" />
                  Published
                </label>
              </div>
            </div>

            <button data-testid="admin-publish-btn" disabled={saving} className="btn-pill btn-primary w-full mt-6">
              <Plus size={14} strokeWidth={1.5} /> {saving ? "Publishing…" : "Publish song"}
            </button>
          </form>

          {/* Library */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="eyebrow mb-1">Library</p>
                <h2 className="font-display text-3xl">{songs.length} songs</h2>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[--sanctum-border] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left p-4">Track</th>
                    <th className="text-left p-4">Genre</th>
                    <th className="text-left p-4">Plays</th>
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {songs.map((s) => (
                    <tr key={s.id} className="border-t border-neutral-100" data-testid={`admin-row-${s.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={s.cover_url} alt="" className="w-10 h-10 rounded-md object-cover" />
                          <div>
                            <div className="font-medium">{s.title}</div>
                            <div className="text-xs text-neutral-500">{s.artist} · {s.album || "Single"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-neutral-600">{s.genre}</td>
                      <td className="p-4 text-neutral-600 tabular-nums">{s.plays}</td>
                      <td className="p-4">
                        <div className="flex justify-end gap-1">
                          <IconBtn testId={`toggle-featured-${s.id}`} label={s.featured ? "Unfeature" : "Feature"}
                            onClick={() => patch(s.id, { featured: !s.featured })}>
                            {s.featured ? <StarOff size={14} strokeWidth={1.5}/> : <Star size={14} strokeWidth={1.5}/>}
                          </IconBtn>
                          <IconBtn testId={`toggle-published-${s.id}`} label={s.published ? "Unpublish" : "Publish"}
                            onClick={() => patch(s.id, { published: !s.published })}>
                            {s.published ? <Eye size={14} strokeWidth={1.5}/> : <EyeOff size={14} strokeWidth={1.5}/>}
                          </IconBtn>
                          <IconBtn testId={`delete-${s.id}`} label="Delete" onClick={() => remove(s.id)}>
                            <Trash2 size={14} strokeWidth={1.5}/>
                          </IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-[--sanctum-border] p-5">
      <div className="flex items-center gap-2 text-neutral-500 text-xs tracking-widest uppercase">
        {icon} {label}
      </div>
      <div className="mt-3 font-display text-3xl tabular-nums">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="eyebrow block mb-1.5">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full h-10 px-3 text-sm rounded-lg border border-[--sanctum-border] focus:outline-none focus:border-neutral-400" />
    </div>
  );
}

function UploadRow({ label, kind, accept, busy, url, onFile, onUrl, optional }) {
  return (
    <div>
      <label className="eyebrow block mb-1.5">{label}{optional && " (optional)"}</label>
      <div className="flex gap-2">
        <input value={url || ""} onChange={(e) => onUrl(e.target.value)} placeholder="Paste URL or upload"
          data-testid={`admin-${kind}-url`}
          className="flex-1 h-10 px-3 text-sm rounded-lg border border-[--sanctum-border] focus:outline-none" />
        <label className="btn-pill btn-outline !py-2 !px-3 text-xs cursor-pointer">
          <Upload size={12} strokeWidth={1.5} /> {busy ? "…" : "File"}
          <input type="file" accept={accept} className="hidden" data-testid={`admin-${kind}-file`}
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, label, testId }) {
  return (
    <button onClick={onClick} title={label} data-testid={testId}
      className="p-2 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100">
      {children}
    </button>
  );
}
